import type { SceneName } from '@maria-cavalheiro-website/shared-types'

import type { TwemojiCodePoint } from './twemoji-manifest.ts'

/**
 * The poem's title plate: which icons compose it, and the lines that ask for
 * them.
 *
 * This is one of the two hand-written halves of the illustration system. The
 * other is `stanza-art.ts`, which illustrates the poem's interior one stanza at a
 * time; this one is its cover. *What stands for what* is nothing but a design
 * decision, so both live in full view, one entry per poem, each carrying the
 * verse it answers to.
 *
 * WHY A COMPOSITION RATHER THAN ONE ICON. A single emoji names a poem's subject
 * and stops there — it can say "cat" but not "a cat who arrived at the house and
 * stayed". Two or three say something a poem might actually be about: the rain
 * AND the horses running through it AND the winged ants the birds hunt
 * afterwards. Each plate is built the same way — a LEAD, which is the poem's
 * subject and is unique across the collection, plus one or two COMPANIONS drawn
 * smaller, which are other things the same poem names.
 *
 * WHERE IT SITS CHANGED IN PHASE 0018. The plate used to be a `position: fixed`
 * mark in the viewport margin, at 0.88 opacity above 72em and at 0.11 in a phone's
 * corner — which is to say invisible to most readers. It is in the flow now,
 * above the poem's title, at full strength, and the stanza figures carry the
 * illustration down the page from there.
 *
 * THE CHOICES ARE SUBJECTS, NOT THEMES. A poem's `theme` is already shown as a
 * chip on its card, and repeating it here would put a leaf beside six different
 * poems. Each lead below names the one thing its poem is about, which is why five
 * bird poems get five different birds and two cat poems get two different cats.
 * Companions are free to repeat: the winged ants belong to *Dia de chuva* and to
 * *Os pardaizinhos* because both poems name them, and drawing that echo is the
 * point rather than an oversight.
 *
 * WHY TWEMOJI. Twitter's emoji set, CC-BY 4.0, drawn as flat filled shapes with
 * soft modelling and no outline. Two properties earn it the job. It reads as
 * illustration rather than as UI, which suits a book of poems about sparrows,
 * clowns and cats; and it carries no black line work, so nothing in it depends
 * on the canvas behind it being pale. Source files are vendored under
 * `public/art/`; the attribution the licence requires is rendered in the footer.
 */

/** What is drawn. Carries no geometry, so it can be placed anywhere. */
export interface PoemFigure {
  readonly codePoint: TwemojiCodePoint
  /** Upstream's own annotation, in English — the name of the thing drawn. */
  readonly annotation: string
}

/**
 * The five authored positions on the plate, reused by every emblem.
 *
 * Bespoke coordinates per poem would be twenty-three layouts to keep balanced
 * against each other, in a mark a few centimetres wide, for no gain a reader
 * could name. One arrangement used consistently reads as a system instead of as
 * twenty-three near-misses — and the variety that matters, which is *what is
 * drawn*, is untouched by it.
 *
 * THE GEOMETRY IS IN `_scenes.sass`, NOT HERE, and that is forced rather than
 * chosen: `style-src 'self'` admits no inline `style` attribute, so a per-figure
 * `inset`/`inline-size` cannot be written onto the element. Five placements is
 * five classes. A name is also the more honest thing to store — `'high'` says
 * what the position is for, where `{ x: 60, y: 1, span: 32 }` only meant
 * something once you knew the canvas was 100 units square.
 *
 * `scene-styles.test.tsx` asserts the stylesheet defines all five, that each one
 * fits inside its box, and that a lead is drawn larger than any companion.
 */
export const PLACEMENTS = Object.freeze([
  'lead-three',
  'lead-two',
  'high',
  'low',
  'solo',
] as const)

export type Placement = (typeof PLACEMENTS)[number]

export interface PlacedFigure extends PoemFigure {
  readonly placement: Placement
}

export interface SceneEmblem {
  /** The poem's subject, drawn largest. Unique across the twenty-three poems. */
  readonly lead: PlacedFigure
  /** One or two smaller things the same poem names. Never empty. */
  readonly companions: readonly PlacedFigure[]
}

/** The lone companion of a pair sits between the two positions a trio uses. */
const LEAD_OF_THREE = { placement: 'lead-three' } as const
const LEAD_OF_TWO = { placement: 'lead-two' } as const
const COMPANION_HIGH = { placement: 'high' } as const
const COMPANION_LOW = { placement: 'low' } as const
const COMPANION_SOLO = { placement: 'solo' } as const

export const SCENE_EMBLEMS: Readonly<Record<SceneName, SceneEmblem>> = Object.freeze({
  // "Bandos velozes, se envolvendo no ar" / "Parecem pérolas no céu azul." The
  // swallows are the only birds in the collection seen purely in flight, and the
  // poem is about the flock rather than the bird — so the bird is drawn twice.
  andorinhas: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F426', annotation: 'bird' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '2601', annotation: 'cloud' },
      { ...COMPANION_LOW, codePoint: '1F426', annotation: 'bird' },
    ],
  },

  // "Estou aqui a observar as árvores" / "E uma joaninha desfilando a sua cor" /
  // "As árvores e suas folhas espalhadas nas beiradas das calçadas."
  arvores: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F333', annotation: 'deciduous tree' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F343', annotation: 'leaf fluttering in wind' },
      { ...COMPANION_LOW, codePoint: '1F41E', annotation: 'lady beetle' },
    ],
  },

  // "Os pingos da chuva deslizam" / "Correm, relincham e estão a saltar" /
  // "Caçam as formigas de asas que saem de suas casas."
  chuva: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F327', annotation: 'cloud with rain' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F434', annotation: 'horse face' },
      { ...COMPANION_LOW, codePoint: '1F41C', annotation: 'ant' },
    ],
  },

  // "As cigarras cantoras e sanfoneiras" — the accordion is the poet's own word
  // for the noise they make, and it beats a musical note for being hers.
  // Upstream has no cicada; the cricket is the same insect in the same role, the
  // one that is heard rather than seen.
  cigarras: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F997', annotation: 'cricket' },
    companions: [{ ...COMPANION_SOLO, codePoint: '1FA97', annotation: 'accordion' }],
  },

  // "Uma escada toda enfeitada, foi planejada para subir" / "A lua surgiu no
  // espaço, a noite estendeu os braços." The moon belongs to *A Lua*, so the
  // night it arrives in is drawn with a star instead.
  escada: {
    lead: { ...LEAD_OF_TWO, codePoint: '1FA9C', annotation: 'ladder' },
    companions: [{ ...COMPANION_SOLO, codePoint: '2B50', annotation: 'star' }],
  },

  // "O dia está muito gelado, o céu está tão nublado" / "Lá no céu um arco-íris
  // brilhou… linda obra que Deus criou." A snowflake was the obvious lead and
  // the wrong one twice over: it is drawn as black outline on nothing, so it
  // vanishes on a dark canvas, and it is not what a cold day in Valinhos looks
  // like. The cold face is legible in both themes and is the poem's first line.
  gelado: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F976', annotation: 'cold face' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '2601', annotation: 'cloud' },
      { ...COMPANION_LOW, codePoint: '1F308', annotation: 'rainbow' },
    ],
  },

  // "Através da janela que dá para o jardim, eu vejo o pé de alecrim" /
  // "Vejo as borboletas a passear, pelas flores a pousar."
  janela: {
    lead: { ...LEAD_OF_THREE, codePoint: '1FA9F', annotation: 'window' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F98B', annotation: 'butterfly' },
      { ...COMPANION_LOW, codePoint: '1F33F', annotation: 'herb' },
    ],
  },

  // "Uma roseira tão florida eu plantei no meu jardim, junto às lindas flores da
  // margarida." The only poem that names the flowers she put there herself.
  jardim: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F33B', annotation: 'sunflower' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F339', annotation: 'rose' },
      { ...COMPANION_LOW, codePoint: '1F33C', annotation: 'blossom' },
    ],
  },

  // "A lua brilha no horizonte" / "A lua entre as nuvens vai sumir" /
  // "Ela vai lá desfilando até lá no imenso mar."
  lua: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F319', annotation: 'crescent moon' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '2601', annotation: 'cloud' },
      { ...COMPANION_LOW, codePoint: '1F30A', annotation: 'water wave' },
    ],
  },

  // "Uma gata tão dengosa, aqui em casa apareceu" / "Ela ganhou uma casinha."
  // Mima is the cat who arrives; Romeu is the cat who is longed for, and they
  // are drawn as different cats.
  mima: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F431', annotation: 'cat face' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F3E0', annotation: 'house' },
      { ...COMPANION_LOW, codePoint: '1F43E', annotation: 'paw prints' },
    ],
  },

  // "O colibri, o macaco, e o jabuti" / "Tem peixes nos pantanais." A survey poem
  // with no single subject, so the lead is growth in the abstract and the
  // companions are two of the specimens it lists. The peacock it also names is
  // drawn with forty-seven shapes and turns to mush at companion scale; the
  // jabuti survives being small, which at 3mm on a phone is the whole job.
  natureza: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F33F', annotation: 'herb' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F422', annotation: 'turtle' },
      { ...COMPANION_LOW, codePoint: '1F41F', annotation: 'fish' },
    ],
  },

  // "A noite está calma, toda estrelada" / "Lá no alto, um avião passando… com
  // grandes asas deslizando." The sky itself, not the moon — *A Lua* has that,
  // and has it as a character.
  noite: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F30C', annotation: 'milky way' },
    companions: [{ ...COMPANION_SOLO, codePoint: '2708', annotation: 'airplane' }],
  },

  // "Sorri com a cara pintada" / "Fica dançando e alegrando a festa."
  palhaco: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F921', annotation: 'clown face' },
    companions: [{ ...COMPANION_SOLO, codePoint: '1F388', annotation: 'balloon' }],
  },

  // "Os pardais estão em festa no pé da jabuticabeira" / "Defendendo seus lares
  // e ninhos." The nest is what tells this flock apart from the fledglings below.
  pardais: {
    lead: { ...LEAD_OF_TWO, codePoint: '1FABA', annotation: 'nest with eggs' },
    companions: [{ ...COMPANION_SOLO, codePoint: '1F347', annotation: 'grapes' }],
  },

  // "Os pardaizinhos são tão lindinhos" / "Caçam as formigas de asas que querem
  // invadir nossas casas."
  pardaizinhos: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F424', annotation: 'baby chick' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F3E0', annotation: 'house' },
      { ...COMPANION_LOW, codePoint: '1F41C', annotation: 'ant' },
    ],
  },

  // "O canarinho fez seu ninho na varanda lá de casa" / "Eles fazem uma
  // orquestra, todos vem cantar aqui."
  passarinhos: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F423', annotation: 'hatching chick' },
    companions: [{ ...COMPANION_SOLO, codePoint: '1F3BA', annotation: 'trumpet' }],
  },

  // "E no pomar também colher os belos frutos no laranjal" / "Não deixe de olhar
  // as flores que brotam com muito vigor." The two concrete images in a poem
  // that is otherwise an argument.
  pomar: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F34A', annotation: 'tangerine' },
    companions: [{ ...COMPANION_SOLO, codePoint: '1F33C', annotation: 'blossom' }],
  },

  // "Elas são simples pombas, que estão ali no telhado" / "Elas saem sobre as
  // casas, em bandos a revoar."
  pombas: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F54A', annotation: 'dove' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F3E0', annotation: 'house' },
      { ...COMPANION_LOW, codePoint: '1F54A', annotation: 'dove' },
    ],
  },

  // "As abelhas estão sobre as florzinhas" / "Coloquei bananas para os
  // sanhaços." The second is the only line in the collection where the poet does
  // something rather than watches something, so it is in the picture.
  quintal: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F41D', annotation: 'honeybee' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F33C', annotation: 'blossom' },
      { ...COMPANION_LOW, codePoint: '1F34C', annotation: 'banana' },
    ],
  },

  // "Romeu, Romeu. Onde você está?" — spoken by a cat, about a cat she loves.
  romeu: {
    lead: { ...LEAD_OF_TWO, codePoint: '1F63B', annotation: 'smiling cat with heart-eyes' },
    companions: [{ ...COMPANION_SOLO, codePoint: '2764', annotation: 'red heart' }],
  },

  // "Uma borboleta colorida por cima da flor passeando" / "A relva está brilhando
  // cheia de gotas de orvalho." The butterfly crosses three of five stanzas; the
  // dew that names the poem is in one, so it is the smaller of the two.
  sereno: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F98B', annotation: 'butterfly' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F4A7', annotation: 'droplet' },
      { ...COMPANION_LOW, codePoint: '1F33C', annotation: 'blossom' },
    ],
  },

  // "A galinha com os pintinhos, pelas gramas saem ciscando" / "A vaca dá de
  // mamar ao terneiro."
  sitio: {
    lead: { ...LEAD_OF_THREE, codePoint: '1F414', annotation: 'chicken' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F424', annotation: 'baby chick' },
      { ...COMPANION_LOW, codePoint: '1F404', annotation: 'cow' },
    ],
  },

  // "O sol apareceu bem cedinho" / "As flores desabrochando estão, as
  // abelhinhas… trabalham muito na polinização."
  sol: {
    lead: { ...LEAD_OF_THREE, codePoint: '2600', annotation: 'sun' },
    companions: [
      { ...COMPANION_HIGH, codePoint: '1F33C', annotation: 'blossom' },
      { ...COMPANION_LOW, codePoint: '1F41D', annotation: 'honeybee' },
    ],
  },
})

/**
 * The figures of one poem's plate, lead first, in paint order.
 *
 * Total by construction: `SceneName` is a validated enum and `SCENE_EMBLEMS` is a
 * `Record` over it, so the lookup cannot miss and needs no fallback arm. That is
 * deliberate rather than lucky — a fallback here would be unreachable code, and
 * unreachable code cannot be covered.
 */
export function emblemParts(name: SceneName): readonly PlacedFigure[] {
  const emblem = SCENE_EMBLEMS[name]

  return [emblem.lead, ...emblem.companions]
}
